import { TimeSeriesPoint } from "~/modules/token_timeseries/types";

// // // //

const TRADING_DAYS_PER_YEAR = 252;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_RETURN_HISTOGRAM_BIN_COUNT = 32;
const DEFAULT_KDE_POINT_COUNT = 120;
const PERCENT_TO_BASIS_POINTS = 100;

/** Minimum calendar-adjusted daily observations before showing the vol chart. */
const MIN_VOLATILITY_OBSERVATIONS = 30;

/** Gaps longer than this between NAV prints are excluded (stale / missing data). */
const MAX_RETURN_GAP_DAYS = 7;

interface ReturnDensityBin {
    minBp: number;
    maxBp: number;
    midpointBp: number;
    density: number;
    count: number;
}

export interface ReturnVolatilityChartStats {
    sigmaBpPerDay: number;
    sigmaAnnualizedPercent: number;
    maxAbsMoveBp: number;
    percentile99LowBp: number;
    percentile99HighBp: number;
    observationCount: number;
    periodStart: string | null;
    periodEnd: string | null;
}

interface ReturnVolatilityDensityPoint {
    xBp: number;
    density: number;
}

interface ReturnVolatilityChartData {
    bins: ReturnDensityBin[];
    kdePoints: ReturnVolatilityDensityPoint[];
    stats: ReturnVolatilityChartStats | null;
    xMinBp: number;
    xMaxBp: number;
    xAxisTickIntervalBp: number;
    xAxisTickPositionsBp: number[];
    binWidthBp: number;
    observationCount: number;
    hasSufficientData: boolean;
    insufficientDataMessage: string | null;
}

interface CalendarAdjustedDailyReturns {
    returnsPercent: number[];
    periodStart: string | null;
    periodEnd: string | null;
}

function percentile(sortedValues: number[], percentileValue: number): number {
    if (sortedValues.length === 0) {
        return 0;
    }

    const index = (percentileValue / 100) * (sortedValues.length - 1);
    const lowerIndex = Math.floor(index);
    const upperIndex = Math.ceil(index);

    if (lowerIndex === upperIndex) {
        return sortedValues[lowerIndex] ?? 0;
    }

    const weight = index - lowerIndex;

    return (
        (sortedValues[lowerIndex] ?? 0) * (1 - weight) +
        (sortedValues[upperIndex] ?? 0) * weight
    );
}

function roundAxisBoundBp(value: number): number {
    if (value <= 8) {
        return 10;
    }

    if (value <= 25) {
        return Math.ceil(value / 5) * 5;
    }

    if (value <= 80) {
        return Math.ceil(value / 10) * 10;
    }

    if (value <= 200) {
        return Math.ceil(value / 25) * 25;
    }

    return Math.ceil(value / 50) * 50;
}

function getNiceTickIntervalBp(axisSpanBp: number): number {
    if (axisSpanBp <= 0) {
        return 5;
    }

    const targetTickCount = 6;
    const roughInterval = axisSpanBp / targetTickCount;
    const magnitude = 10 ** Math.floor(Math.log10(roughInterval));
    const normalized = roughInterval / magnitude;

    let niceFactor = 10;

    if (normalized <= 1) {
        niceFactor = 1;
    } else if (normalized <= 2) {
        niceFactor = 2;
    } else if (normalized <= 5) {
        niceFactor = 5;
    }

    return niceFactor * magnitude;
}

/**
 * Symmetric x-axis snapped to round tick marks so labels land on clean bp values.
 */
function computeVolatilityChartAxisBoundsBp(props: {
    sigmaBpPerDay: number;
    maxAbsMoveBp: number;
    percentile99LowBp: number;
    percentile99HighBp: number;
}): {
    xMinBp: number;
    xMaxBp: number;
    xAxisTickIntervalBp: number;
    xAxisTickPositionsBp: number[];
} {
    const percentileSpanBp = Math.max(
        Math.abs(props.percentile99LowBp),
        Math.abs(props.percentile99HighBp),
    );

    const rawBoundBp = Math.max(
        props.maxAbsMoveBp,
        percentileSpanBp * 1.05,
        props.sigmaBpPerDay * 3.5,
        8,
    );
    const initialBoundBp = roundAxisBoundBp(rawBoundBp);
    const maxTickCount = 7;
    let tickIntervalBp = getNiceTickIntervalBp(initialBoundBp * 2);

    while ((initialBoundBp * 2) / tickIntervalBp > maxTickCount) {
        tickIntervalBp = getNiceTickIntervalBp(
            (initialBoundBp * 2) / (maxTickCount - 1),
        );
    }

    const snappedBoundBp =
        Math.ceil(initialBoundBp / tickIntervalBp) * tickIntervalBp;
    const xMinBp = -snappedBoundBp;
    const xMaxBp = snappedBoundBp;
    const xAxisTickPositionsBp: number[] = [];

    for (
        let tickBp = xMinBp;
        tickBp <= xMaxBp + tickIntervalBp * 0.001;
        tickBp += tickIntervalBp
    ) {
        xAxisTickPositionsBp.push(Math.round(tickBp));
    }

    return {
        xMinBp,
        xMaxBp,
        xAxisTickIntervalBp: tickIntervalBp,
        xAxisTickPositionsBp,
    };
}

function computeStandardDeviation(values: number[]): number {
    if (values.length < 2) {
        return 0;
    }

    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const variance =
        values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
        (values.length - 1);

    return Math.sqrt(Math.max(variance, 0));
}

function computeSilvermanBandwidth(values: number[]): number {
    if (values.length < 2) {
        return 1;
    }

    const sortedValues = [...values].sort((a, b) => a - b);
    const standardDeviation = computeStandardDeviation(values);
    const interquartileRange =
        percentile(sortedValues, 75) - percentile(sortedValues, 25);
    const scale = Math.min(
        standardDeviation,
        interquartileRange > 0 ? interquartileRange / 1.34 : standardDeviation,
    );

    return Math.max(0.9 * scale * values.length ** -0.2, 1e-6);
}

function computeGaussianKernelDensity(
    values: number[],
    evaluationPoints: number[],
): number[] {
    if (values.length === 0) {
        return evaluationPoints.map(() => 0);
    }

    const bandwidth = computeSilvermanBandwidth(values);
    const sampleCount = values.length;
    const normalizingConstant = sampleCount * bandwidth;

    return evaluationPoints.map((point) => {
        let density = 0;

        for (const value of values) {
            const standardizedDistance = (point - value) / bandwidth;
            density += Math.exp(-0.5 * standardizedDistance ** 2);
        }

        return density / (normalizingConstant * Math.sqrt(2 * Math.PI));
    });
}

function percentReturnToBasisPoints(percentReturn: number): number {
    return percentReturn * PERCENT_TO_BASIS_POINTS;
}

function basisPointsToPercent(basisPoints: number): number {
    return basisPoints / PERCENT_TO_BASIS_POINTS;
}

function computeAnnualizedVolatilityFromBp(
    dailyReturnsBp: number[],
): number | null {
    if (dailyReturnsBp.length < 2) {
        return null;
    }

    const dailyReturnsPercent = dailyReturnsBp.map(basisPointsToPercent);
    return computeAnnualizedVolatility(dailyReturnsPercent);
}

function computeAnnualizedVolatility(dailyReturns: number[]): number | null {
    if (dailyReturns.length < 2) {
        return null;
    }

    const mean =
        dailyReturns.reduce((sum, value) => sum + value, 0) /
        dailyReturns.length;
    const variance =
        dailyReturns.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
        (dailyReturns.length - 1);

    if (variance <= 0) {
        return 0;
    }

    return Math.sqrt(variance) * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

function getCalendarAdjustedDailyReturns(
    points: TimeSeriesPoint[],
): CalendarAdjustedDailyReturns {
    const returnsPercent: number[] = [];
    let periodStart: string | null = null;
    let periodEnd: string | null = null;

    for (let index = 1; index < points.length; index++) {
        const previousPoint = points[index - 1];
        const currentPoint = points[index];
        const previousValue = previousPoint[1];
        const currentValue = currentPoint[1];

        if (previousValue <= 0 || currentValue <= 0) {
            continue;
        }

        const elapsedDays = Math.max(
            1,
            Math.round(
                (new Date(currentPoint[0]).getTime() -
                    new Date(previousPoint[0]).getTime()) /
                    MS_PER_DAY,
            ),
        );

        if (elapsedDays > MAX_RETURN_GAP_DAYS) {
            continue;
        }

        const periodReturn = currentValue / previousValue - 1;
        const dailyEquivalentPercent =
            (Math.pow(1 + periodReturn, 1 / elapsedDays) - 1) * 100;

        returnsPercent.push(dailyEquivalentPercent);

        if (periodStart === null) {
            periodStart = currentPoint[0];
        }

        periodEnd = currentPoint[0];
    }

    return {
        returnsPercent,
        periodStart,
        periodEnd,
    };
}

function assignReturnToBin(props: {
    returnBp: number;
    bins: ReturnDensityBin[];
    xMinBp: number;
    xMaxBp: number;
    binWidthBp: number;
    binCount: number;
}) {
    const { returnBp, bins, xMinBp, xMaxBp, binWidthBp, binCount } = props;
    const clampedReturnBp = Math.max(xMinBp, Math.min(xMaxBp, returnBp));
    const binIndex = Math.min(
        Math.floor((clampedReturnBp - xMinBp) / binWidthBp),
        binCount - 1,
    );

    if (binIndex >= 0) {
        bins[binIndex].count++;
    }
}

export function computeReturnVolatilityDensityChart(
    points: TimeSeriesPoint[],
    binCount = DEFAULT_RETURN_HISTOGRAM_BIN_COUNT,
): ReturnVolatilityChartData {
    const { returnsPercent, periodStart, periodEnd } =
        getCalendarAdjustedDailyReturns(points);
    const dailyReturnsBp = returnsPercent.map(percentReturnToBasisPoints);
    const observationCount = dailyReturnsBp.length;

    if (observationCount < MIN_VOLATILITY_OBSERVATIONS) {
        return {
            bins: [],
            kdePoints: [],
            stats: null,
            xMinBp: -10,
            xMaxBp: 10,
            xAxisTickIntervalBp: 5,
            xAxisTickPositionsBp: [-10, -5, 0, 5, 10],
            binWidthBp: 0,
            observationCount,
            hasSufficientData: false,
            insufficientDataMessage:
                observationCount === 0
                    ? "No return history is available for this asset."
                    : `At least ${MIN_VOLATILITY_OBSERVATIONS} daily observations are needed to estimate return volatility (${observationCount} available).`,
        };
    }

    const sortedReturnsBp = [...dailyReturnsBp].sort((a, b) => a - b);
    const maxAbsMoveBp = Math.max(...dailyReturnsBp.map(Math.abs));
    const sigmaBpPerDay = computeStandardDeviation(dailyReturnsBp);
    const percentile99LowBp = percentile(sortedReturnsBp, 0.5);
    const percentile99HighBp = percentile(sortedReturnsBp, 99.5);

    const { xMinBp, xMaxBp, xAxisTickIntervalBp, xAxisTickPositionsBp } =
        computeVolatilityChartAxisBoundsBp({
            sigmaBpPerDay,
            maxAbsMoveBp,
            percentile99LowBp,
            percentile99HighBp,
        });
    const binWidthBp = (xMaxBp - xMinBp) / binCount;

    const bins: ReturnDensityBin[] = Array.from(
        { length: binCount },
        (_, index) => {
            const minBp = xMinBp + index * binWidthBp;
            const maxBp = xMinBp + (index + 1) * binWidthBp;

            return {
                minBp,
                maxBp,
                midpointBp: (minBp + maxBp) / 2,
                density: 0,
                count: 0,
            };
        },
    );

    dailyReturnsBp.forEach((dailyReturnBp) => {
        assignReturnToBin({
            returnBp: dailyReturnBp,
            bins,
            xMinBp,
            xMaxBp,
            binWidthBp,
            binCount,
        });
    });

    bins.forEach((bin) => {
        bin.density = bin.count / (observationCount * binWidthBp);
    });

    const evaluationPoints = Array.from(
        { length: DEFAULT_KDE_POINT_COUNT },
        (_, index) =>
            xMinBp +
            (index / (DEFAULT_KDE_POINT_COUNT - 1)) * (xMaxBp - xMinBp),
    );
    const kdeDensities = computeGaussianKernelDensity(
        dailyReturnsBp,
        evaluationPoints,
    );
    const kdePoints = evaluationPoints.map((xBp, index) => ({
        xBp,
        density: kdeDensities[index] ?? 0,
    }));

    const sigmaAnnualizedPercent =
        computeAnnualizedVolatilityFromBp(dailyReturnsBp) ?? 0;

    const stats: ReturnVolatilityChartStats = {
        sigmaBpPerDay,
        sigmaAnnualizedPercent,
        maxAbsMoveBp,
        percentile99LowBp,
        percentile99HighBp,
        observationCount,
        periodStart,
        periodEnd,
    };

    return {
        bins,
        kdePoints,
        stats,
        xMinBp,
        xMaxBp,
        xAxisTickIntervalBp,
        xAxisTickPositionsBp,
        binWidthBp,
        observationCount,
        hasSufficientData: true,
        insufficientDataMessage: null,
    };
}

export function formatXAxisBpLabel(value: number | string): string {
    const roundedBp = Math.round(Number(value));

    if (roundedBp === 0) {
        return "0";
    }

    return roundedBp.toLocaleString("en-US");
}

export function formatBasisPoints(value: number): string {
    return `${Math.round(value)}`;
}

export function formatSignedBasisPoints(value: number): string {
    const rounded = Math.round(value);
    if (rounded > 0) {
        return `+${rounded}`;
    }

    return `${rounded}`;
}
